<template>
    <div>
        <div v-click-outside="closeMenu" class="ActionMenu" :class="['rounded', { isOpen }]">
            <BaseButton
                ref="menuButton"
                :aria-label="localizations[lang].tooltips.moreActions"
                icon="baseline-more_vert-24px"
                class="flatButton"
                @click="toggleMenu"
            />
            <div
                v-if="isOpen"
                ref="menuContainer"
                class="fixed z-999 flex items-start gap-1.5"
                :class="{ 'flex-row-reverse': onlyMobileVisible }"
                :style="menuPosition"
            >
                <!-- Main menu panel -->
                <div class="action-menu-panel">
                    <ul class="m-0 list-none rounded p-0">
                        <ListItemButton
                            v-if="!isAnnotation && copyContentToClipboard.enabled"
                            ref="copyTrigger"
                            icon-name="quote-24px"
                            @click="toggleCopyMenu"
                        >
                            <span>{{ localizations[lang].citation.menu.title }}</span>
                            <svg-icon class="ml-auto" icon="chevron-right" width="16px" height="16px" />
                        </ListItemButton>
                        <ListItemButton
                            v-if="!isAnnotation && AiIntegrations.enabled"
                            ref="aiTrigger"
                            icon-custom-fill="#FF26E9"
                            icon-name="sparkles"
                            @click="toggleAIMenu"
                        >
                            {{ localizations[lang]['ai-integrations'].menu.title }}
                            <svg-icon class="ml-auto" icon="chevron-right" width="16px" height="16px" />
                        </ListItemButton>
                        <ListItemButton
                            v-if="copyContentToClipboard.enabled"
                            icon-name="copy"
                            @click="copyPlainToClipboard"
                        >
                            {{ localizations[lang].onlyCopy }}
                        </ListItemButton>
                        <ListItemButton
                            v-if="isAnnotation && !isSharedSelection"
                            icon-name="baseline-edit-24px"
                            dusk="EditNote"
                            @click="editNote"
                        >
                            {{ localizations[lang].editNote }}
                        </ListItemButton>
                        <ListItemButton icon-name="translate-24px" @click="translateText">
                            {{ localizations[lang].translate }}
                        </ListItemButton>
                        <ListItemButton icon-name="speaker-24px" @click="listeningHighlight">
                            {{ localizations[lang].listening.label }}
                        </ListItemButton>
                        <ListItemButton
                            v-if="!isSharedSelection"
                            ref="colorTrigger"
                            icon-name="ic-change-color"
                            @click="toggleColorMenu"
                        >
                            <span>{{ localizations[lang].changeColor }}</span>
                            <svg-icon class="ml-auto" icon="chevron-right" width="16px" height="16px" />
                        </ListItemButton>
                        <SocialShareButton @share="closeMenuOnShare" />
                        <ListItemButton
                            v-if="isSelectionDeletable && !isSharedSelection"
                            icon-name="baseline-delete-24px"
                            danger
                            @click="deleteSelection"
                        >
                            {{ localizations[lang].delete }}
                        </ListItemButton>
                    </ul>
                    <div v-if="sharedByEmail" class="action-menu-shared-by">
                        {{ localizations[lang].sharing.sharedBy }}:
                        <span class="action-menu-shared-by-email">{{ sharedByEmail }}</span>
                    </div>
                    <SocialShare
                        v-if="menuShareIsShowing"
                        :share-title="localizations[lang].socialShare"
                        :url-for-share="issueHighlightUrlForShare"
                        @close="closeShareMenu"
                    />
                </div>

                <!-- Citation submenu -->
                <div
                    v-if="menuCopyIsShowing"
                    class="action-menu-submenu"
                    :style="{ marginTop: submenuOffsetTop + 'px' }"
                >
                    <ul class="m-0 list-none p-0">
                        <template v-for="format in availableCitationFormats">
                            <!-- Chicago with submenu arrow -->
                            <ListItemButton
                                v-if="format === 'Chicago'"
                                :key="format + '-submenu'"
                                ref="chicagoTrigger"
                                @click="toggleChicagoMenu"
                            >
                                <span>{{ localizations[lang].citation.menu[format] }}</span>
                                <svg-icon class="ml-auto" icon="chevron-right" width="16px" height="16px" />
                            </ListItemButton>
                            <!-- Other formats -->
                            <ListItemButton v-else :key="format" @click="copyCitationToClipboard(format)">
                                {{ localizations[lang].citation.menu[format] }}
                            </ListItemButton>
                        </template>
                    </ul>
                </div>

                <!-- Chicago submenu -->
                <div
                    v-if="menuChicagoIsShowing"
                    class="action-menu-submenu"
                    :style="{ marginTop: chicagoSubmenuOffsetTop + 'px' }"
                >
                    <ul class="m-0 list-none p-0">
                        <ListItemButton
                            v-for="subformat in chicagoSubformats"
                            :key="subformat"
                            @click="copyCitationToClipboard(subformat)"
                        >
                            {{ localizations[lang].citation.menu[subformat] }}
                        </ListItemButton>
                    </ul>
                </div>

                <!-- AI submenu -->
                <div v-if="menuAiIsShowing" class="action-menu-submenu" :style="{ marginTop: submenuOffsetTop + 'px' }">
                    <ul class="m-0 list-none p-0">
                        <ListItemButton
                            v-for="(actionText, action) in aiActions"
                            :key="action"
                            @click="showAiIntegration(action)"
                        >
                            {{ actionText }}
                        </ListItemButton>
                    </ul>
                </div>

                <!-- Color submenu -->
                <div
                    v-if="menuColorIsShowing"
                    class="action-menu-submenu"
                    :style="{ marginTop: submenuOffsetTop + 'px' }"
                >
                    <div class="grid grid-cols-5 gap-1.5">
                        <button
                            v-for="color in allHighlighters"
                            :key="color.slug"
                            v-tooltip="localizations[lang][color.label] || color.label"
                            :aria-label="localizations[lang][color.label] || color.label"
                            :aria-pressed="selection.colorSlug === color.slug"
                            type="button"
                            class="action-menu-color-swatch"
                            :class="{ 'is-active': selection.colorSlug === color.slug }"
                            @click="changeColor(color.color, color.slug)"
                        >
                            <span
                                class="block h-5 w-5 rounded-full transition-all duration-150"
                                :style="{ backgroundColor: color.color }"
                            ></span>
                            <span
                                v-if="selection.colorSlug === color.slug"
                                class="absolute h-2 w-2 rounded-full bg-theme-bg"
                            ></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Citation Mobile Modal -->
        <CitationMobileModal
            :is-open="showCitationMobileModal"
            :title="localizations[lang].citation.menu.title"
            :formats="availableCitationFormats"
            :chicago-subformats="chicagoSubformats"
            :chicago-expanded="chicagoMobileExpanded"
            :labels="localizations[lang].citation.menu"
            @close="closeCitationMobileModal"
            @toggle-chicago="chicagoMobileExpanded = !chicagoMobileExpanded"
            @select="handleCitationMobileSelect"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import citationFormats, { getAvailableFormats } from '_shared_/js/utils/citationFormats.js';
import ListItemButton from '_shared_/UI/Components/list/ListItemButton.vue';
import SocialShareButton from '_shared_/UI/SocialShare/SocialShareButton.vue';
import SocialShare from '_shared_/UI/SocialShare/SocialShare.vue';
import BaseButton from '../../Components/buttons/BaseButton.vue';
import CitationMobileModal from '../../Components/CitationMobileModal.vue';
import { uiAPI } from '../../../../src/js/communication';
import { pendingHighlightDeletes } from '_shared_/js/utils/pendingHighlightDeletes.js';

export default {
    components: {
        BaseButton,
        ListItemButton,
        SocialShareButton,
        SocialShare,
        CitationMobileModal,
    },
    props: {
        selection: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isOpen: false,
            citationWasCopied: false,
            plainCopied: false,
            menuCopyIsShowing: false,
            menuChicagoIsShowing: false,
            menuAiIsShowing: false,
            menuColorIsShowing: false,
            menuShareIsShowing: false,
            submenuOffsetTop: 0,
            chicagoSubmenuOffsetTop: 0,
            fixedTop: 0,
            fixedLeft: 0,
            fixedRight: 0,
            showCitationMobileModal: false,
            chicagoMobileExpanded: false,
        };
    },
    computed: {
        ...mapState([
            'ui',
            'localizations',
            'lang',
            'issue',
            'anonymousSession',
            'title',
            'lastLocation',
            'AiIntegrations',
            'copyContentToClipboard',
            'aiActions',
            'sharedColors',
        ]),
        ...mapGetters([
            'currentPage',
            'isEpub',
            'onlyMobileVisible',
            'canUseNativeShare',
            'socialShareDisabled',
            'allHighlighters',
        ]),

        /**
         * Shared color (if any) that matches the current selection's slug.
         * @returns {Object|undefined}
         */
        sharedColorForSelection() {
            return this.sharedColors.find(color => color.slug === this.selection.colorSlug);
        },

        /**
         * Whether the selection belongs to a shared color (from another user).
         */
        isSharedSelection() {
            return Boolean(this.sharedColorForSelection);
        },

        /**
         * Email of the author who shared the color the current selection uses.
         * Undefined for own selections.
         */
        sharedByEmail() {
            return this.sharedColorForSelection?.author?.email;
        },

        menuPosition() {
            if (this.onlyMobileVisible) {
                return {
                    top: `${this.fixedTop}px`,
                    right: `${this.fixedRight}px`,
                };
            }
            return {
                top: `${this.fixedTop}px`,
                left: `${this.fixedLeft}px`,
            };
        },

        /**
         * Whether the annotations (notebook) panel is currently open.
         * @returns {boolean}
         */
        notebookOpened() {
            return this.ui.notebook.opened;
        },

        /**
         * Signature of which submenus (citation, Chicago, AI, color, share) are open.
         * Changes on every submenu transition — including submenu→submenu switches that
         * keep "any open" true — so the viewport re-check runs whenever the menu height changes.
         * @returns {string}
         */
        openSubmenusSignature() {
            return [
                this.menuCopyIsShowing,
                this.menuChicagoIsShowing,
                this.menuAiIsShowing,
                this.menuColorIsShowing,
                this.menuShareIsShowing,
            ].join('');
        },

        chicagoSubformats() {
            return getAvailableFormats(citationFormats.chicagoSubformats, this.issue, this.title);
        },

        availableCitationFormats() {
            const formats = getAvailableFormats(citationFormats.formats, this.issue, this.title);

            if (this.chicagoSubformats.length === 0) {
                return formats.filter(format => format !== 'Chicago');
            }

            return formats;
        },

        issueHighlightUrlForShare() {
            const location = this.isEpub ? btoa(JSON.stringify(this.selection.epubLocation)) : this.selection.startPage;
            return `${this.issue.issue_url}?location=${location}`;
        },

        selectionColor: {
            get() {
                return this.selection.color;
            },
            set(value) {
                this.changeColor(value);
            },
        },

        isAnnotation() {
            return this.selection.hasAnnotation;
        },

        isSelectionDeletable() {
            return this.selection.canBeDeleted();
        },

        textToSpeechOrTranslate() {
            if (this.isAnnotation) {
                return `${this.localizations[this.lang].highlight}: ${this.selection.totalText}; \n\n ${
                    this.localizations[this.lang].note
                }: ${this.selection.annotationText}`;
            }
            return this.selection.totalText;
        },

        textForShare() {
            return `"${this.selection.totalText} ${this.truncateLongText(this.title, 100)}" ${
                this.localizations[this.lang].in
            } ${this.issueHighlightUrlForShare}`;
        },
    },

    watch: {
        isOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    const el = this.$refs.menuContainer;
                    if (el && this._portalTarget) {
                        this._portalTarget.appendChild(el);
                    }
                    this.keepMenuWithinViewport();
                });
            }
        },
        notebookOpened(opened) {
            if (!opened) {
                this.closeMenu();
            }
        },
        openSubmenusSignature() {
            if (this.isOpen) {
                this.$nextTick(this.keepMenuWithinViewport);
            }
        },
    },

    mounted() {
        this._portalTarget = document.createElement('div');
        this._portalTarget.className = 'relative z-999';
        const reader = this.$el.closest('.Reader') || document.body;
        reader.appendChild(this._portalTarget);
        this.$bus.$on('confirmDeleteHighlight', this.handleConfirmDelete);
    },

    beforeDestroy() {
        this.closeMenu();
        this.closeCitationMobileModal();
        this.$bus.$off('confirmDeleteHighlight', this.handleConfirmDelete);
        if (this._portalTarget && this._portalTarget.parentNode) {
            this._portalTarget.parentNode.removeChild(this._portalTarget);
        }
    },

    methods: {
        ...mapMutations([
            'openToast',
            'removeSelection',
            'setCurrentHighlight',
            'updateSelection',
            'openSocialShare',
            'openConfirmationModal',
        ]),
        ...mapActions([
            'trackEvent',
            'listeningHighlights',
            'copyToClipBoard',
            'openTranslateIntegration',
            'openAiIntegration',
            'toggleNotebook',
            'openNoteEditor',
        ]),

        /** The note opens for editing where it already is, in the list. */
        editNote() {
            this.closeMenu();
            this.openNoteEditor({ uuid: this.selection.uuid });
        },

        /**
         * Closes the notebook on mobile, where it covers the reader. Only an open notebook
         * is toggled: the delete path reaches here after awaiting the host, and toggling a
         * notebook the user closed in the meantime would reopen it.
         */
        // Closing the notebook drops the note being written, so it stays for that.
        closeSidebarOnMobile() {
            if (this.onlyMobileVisible && this.ui.notebook.opened && !this.ui.noteEditor.opened) {
                this.toggleNotebook();
            }
        },

        openMenu() {
            const btn = this.$refs.menuButton.$el || this.$refs.menuButton;
            const rect = btn.getBoundingClientRect();
            this.fixedTop = rect.top;

            if (this.onlyMobileVisible) {
                this.fixedRight = window.innerWidth - rect.right;
            } else {
                this.fixedLeft = rect.right + 4;
            }

            this.isOpen = true;
            this.resetSubmenus();
        },

        /**
         * Keeps the floating menu fully within the viewport by shifting its top
         * up when it would overflow the bottom edge. Anchored to the trigger's
         * top by default; only adjusts when there isn't enough room below — fixes
         * the last-item options being clipped off the bottom of the screen.
         * @returns {void}
         */
        keepMenuWithinViewport() {
            const menu = this.$refs.menuContainer;
            if (!menu) return;
            const VIEWPORT_MARGIN = 8;
            const menuHeight = menu.getBoundingClientRect().height;
            const maxTop = window.innerHeight - menuHeight - VIEWPORT_MARGIN;
            if (this.fixedTop > maxTop) {
                this.fixedTop = Math.max(VIEWPORT_MARGIN, maxTop);
            }
        },

        resetSubmenus() {
            this.menuCopyIsShowing = false;
            this.menuChicagoIsShowing = false;
            this.menuAiIsShowing = false;
            this.menuColorIsShowing = false;
            this.menuShareIsShowing = false;
        },

        closeCitationMobileModal() {
            this.showCitationMobileModal = false;
            this.chicagoMobileExpanded = false;
        },

        handleCitationMobileSelect(format) {
            this.closeCitationMobileModal();
            this.copyCitationToClipboard(format);
        },

        getSubmenuOffset(refName) {
            const trigger = this.$refs[refName]?.$el || this.$refs[refName];
            if (trigger) {
                const panel = trigger.closest('.action-menu-panel');
                return trigger.offsetTop - (panel ? panel.offsetTop : 0);
            }
            return 0;
        },

        getChicagoSubmenuOffset() {
            const trigger = this.$refs.chicagoTrigger;
            const element = Array.isArray(trigger) ? trigger[0] : trigger;
            const el = element?.$el || element;
            if (el) {
                const submenu = el.closest('.action-menu-submenu');
                return this.submenuOffsetTop + el.offsetTop - (submenu ? submenu.offsetTop : 0);
            }
            return this.submenuOffsetTop;
        },

        handleShare() {
            if (this.socialShareDisabled) return;
            if (this.canUseNativeShare) {
                this.shareUsingShareApi();
                this.closeMenu();
                this.closeSidebarOnMobile();
            } else {
                this.menuShareIsShowing = true;
            }
        },

        shareUsingShareApi() {
            if (this.socialShareDisabled) return;
            const text = this.truncateLongText(this.issue.descriptionRawText || '', 90);
            uiAPI.shareContent({
                title: `${this.localizations[this.lang].imReading} ${this.title}`,
                text,
                url: this.issueHighlightUrlForShare,
            });
        },

        closeShareMenu() {
            this.menuShareIsShowing = false;
        },

        /**
         * Closes the action menu when sharing through our custom modal, so the
         * still-open menu doesn't paint over the centered share dialog. On the
         * native OS share path (mobile / embedded app) it leaves the menu and
         * notebook untouched — the system share sheet overlays the screen on its
         * own, and closing here would needlessly dismiss the notebook.
         * @returns {void}
         */
        closeMenuOnShare() {
            if (this.canUseNativeShare) {
                return;
            }
            this.closeMenu();
        },

        toggleCopyMenu() {
            if (this.onlyMobileVisible) {
                this.closeMenu();
                this.showCitationMobileModal = true;
            } else {
                this.resetSubmenus();
                this.submenuOffsetTop = this.getSubmenuOffset('copyTrigger');
                this.menuCopyIsShowing = true;
            }
        },

        toggleChicagoMenu() {
            this.$nextTick(() => {
                this.chicagoSubmenuOffsetTop = this.getChicagoSubmenuOffset();
                this.menuChicagoIsShowing = true;
            });
        },

        toggleAIMenu() {
            this.resetSubmenus();
            this.submenuOffsetTop = this.getSubmenuOffset('aiTrigger');
            this.menuAiIsShowing = true;
        },

        toggleColorMenu() {
            this.resetSubmenus();
            this.submenuOffsetTop = this.getSubmenuOffset('colorTrigger');
            this.menuColorIsShowing = true;
        },

        toggleMenu() {
            if (this.isOpen) {
                this.closeMenu();
            } else {
                this.openMenu();
            }
        },

        closeMenu(event) {
            if (event && this._portalTarget && this._portalTarget.contains(event.target)) {
                return;
            }
            this.isOpen = false;
        },

        deleteSelection() {
            this.openConfirmationModal({
                title: this.localizations[this.lang].confirmDelete.title,
                message: this.localizations[this.lang].confirmDelete.message,
                confirmLabel: this.localizations[this.lang].delete,
                cancelLabel: this.localizations[this.lang].cancel,
                onConfirmAction: 'confirmDeleteHighlight',
                context: {
                    uuid: this.selection.uuid,
                    instanceId: this._uid,
                    cssClass: this.selection.getUniqueCssClass(),
                    selection: this.selection,
                },
            });
        },

        /**
         * Deletes the confirmed highlight. The host owns it, so the list only changes once
         * the host confirms: a refused delete toasts and leaves the highlight in place (it
         * would come back on the next load anyway), mirroring deleteBookmark. Every notebook
         * tab keeps its own menu for the highlight and all of them hear the confirmation, so
         * only the menu that asked for it deletes and the host gets a single request.
         */
        async handleConfirmDelete(context) {
            if (context.uuid !== this.selection.uuid || context.instanceId !== this._uid) {
                return;
            }

            // A second confirmation while the host has not answered, from this menu or from
            // the selection tool, would send a duplicate delete, and its refusal would toast
            // a failure for a highlight already gone.
            if (pendingHighlightDeletes.has(context.uuid)) {
                return;
            }

            pendingHighlightDeletes.add(context.uuid);
            try {
                if (!this.anonymousSession) {
                    const deleted = await context.selection.destroy();
                    if (!deleted) {
                        this.openToast({ text: this.localizations[this.lang].highlightDeletionFailed });
                        return;
                    }
                }
                this.$store.commit('removeSelection', {
                    uuid: context.uuid,
                });
                this.$bus.$emit('selectionDestroyed', context.cssClass);
                this.closeMenu();
                this.closeSidebarOnMobile();
                this.trackEvent({
                    action: 'Highlight - delete',
                    trigger: 'click',
                    page: '',
                    source: 'action menu',
                });
            } finally {
                pendingHighlightDeletes.release(context.uuid);
            }
        },

        changeColor(color, colorSlug) {
            const selectionToUpdate = Object.assign({}, this.selection);
            this.$bus.$emit('applyColorAndConfirmSelection', { color, colorSlug }, selectionToUpdate);

            this.updateSelection(selectionToUpdate);
            this.closeMenu();
            this.closeSidebarOnMobile();
            this.trackEvent({
                action: 'Highlight - update',
                trigger: 'click',
                page: '',
                source: 'action menu',
            });
        },

        truncateLongText(text, length = 140) {
            if (text.length > length) {
                return `${text.substring(0, length)}...`;
            }
            return text;
        },

        async copyHighlight() {
            const wasCopied = await this.copyToClipBoard(this.textForShare);
            if (!wasCopied) return;

            this.openToast({ text: this.localizations[this.lang].copyUrlToClipboard });
        },

        async copyCitationToClipboard(format) {
            if (this.citationWasCopied) return;
            const currentLocation = this.isEpub ? this.selection.epubLocation : this.selection.startPage;

            const formattedSelectionText = citationFormats[format](
                this.selection.totalText,
                this.issue,
                currentLocation,
                this.title,
                this.isEpub,
                this.localizations[this.lang]
            );
            const wasCopied = await this.copyToClipBoard(formattedSelectionText);
            if (!wasCopied) return;

            this.citationWasCopied = true;
            this.openToast({ text: this.localizations[this.lang].citation.messages.copied });
            this.closeMenu();
            this.closeSidebarOnMobile();

            this.trackEvent({
                action: `Citation - ${format}`,
                trigger: 'click',
                page: '',
                source: 'action menu',
            });

            setTimeout(() => {
                this.citationWasCopied = false;
            }, 1000);
        },

        showAiIntegration(action) {
            this.closeMenu();
            this.closeSidebarOnMobile();
            this.openAiIntegration({ text: this.selection.totalText, action });
        },

        async copyPlainToClipboard() {
            if (this.plainCopied) return;
            const wasCopied = await this.copyToClipBoard(this.selection.totalText);
            if (!wasCopied) return;

            this.plainCopied = true;
            this.openToast({ text: this.localizations[this.lang].plainCopied });
            this.closeMenu();
            this.closeSidebarOnMobile();

            setTimeout(() => {
                this.plainCopied = false;
            }, 1000);
        },

        translateText() {
            this.openTranslateIntegration({
                text: this.textToSpeechOrTranslate,
            });
            this.closeMenu();
            this.closeSidebarOnMobile();
        },

        listeningHighlight() {
            this.listeningHighlights({
                text: this.textToSpeechOrTranslate,
                uuidToListen: this.selection.uuid,
            });
            this.closeMenu();
            this.trackEvent({
                action: 'Highlight - listening',
                trigger: 'click',
                page: '',
                source: 'action menu',
            });
        },
    },
};
</script>

<style>
.action-menu-panel {
    @apply overflow-hidden rounded-lg bg-theme-bg text-theme-text;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.12),
        0 1px 3px rgba(0, 0, 0, 0.08);
}

.action-menu-submenu {
    @apply rounded-lg bg-theme-bg p-2 text-theme-text;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.12),
        0 1px 3px rgba(0, 0, 0, 0.08);
}

.action-menu-color-swatch {
    @apply relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-2 border-transparent bg-transparent p-0 transition-all duration-150 hover:scale-110 focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2;
}

.action-menu-shared-by {
    @apply break-all border-t border-theme-border px-3 py-2 text-[11px] leading-tight text-theme-toolbar-icon;
}

.action-menu-shared-by-email {
    @apply font-medium text-theme-text;
}

.action-menu-color-swatch.is-active {
    @apply border-[var(--theme-text-color)];
}

.Reader[data-theme='dark'] .action-menu-panel,
.Reader[data-theme='dark'] .action-menu-submenu {
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.4),
        0 1px 3px rgba(0, 0, 0, 0.2);
}

.Reader[data-theme='high-contrast-dark'] .action-menu-panel,
.Reader[data-theme='high-contrast-dark'] .action-menu-submenu {
    @apply border border-white shadow-none;
}
</style>
